import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';

type RecaptchaApi = {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: RecaptchaApi;
  }
}

@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private readonly document = inject(DOCUMENT);
  private loader?: Promise<void>;

  async execute(action: string): Promise<string> {
    const siteKey = environment.recaptchaSiteKey;
    if (!siteKey) {
      throw new Error('reCAPTCHA site key is not configured.');
    }

    await this.load(siteKey);

    const recaptcha = this.document.defaultView?.grecaptcha;
    if (!recaptcha) {
      throw new Error('reCAPTCHA failed to load.');
    }

    return new Promise<string>((resolve, reject) => {
      recaptcha.ready(() => {
        recaptcha.execute(siteKey, { action }).then(resolve, reject);
      });
    });
  }

  private load(siteKey: string): Promise<void> {
    if (this.document.defaultView?.grecaptcha) {
      return Promise.resolve();
    }

    if (this.loader) {
      return this.loader;
    }

    this.loader = new Promise<void>((resolve, reject) => {
      const script = this.document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        this.loader = undefined;
        script.remove();
        reject(new Error('Unable to load reCAPTCHA.'));
      };
      this.document.head.appendChild(script);
    });

    return this.loader;
  }
}
